import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { TimePicker } from '@/components/ui/time-picker';
import { Loader2, Clock, Copy } from 'lucide-react';
import { toast } from 'sonner';
interface Schedule {
  monday: {
    open: string | null;
    close: string | null;
    closed: boolean;
  };
  tuesday: {
    open: string | null;
    close: string | null;
    closed: boolean;
  };
  wednesday: {
    open: string | null;
    close: string | null;
    closed: boolean;
  };
  thursday: {
    open: string | null;
    close: string | null;
    closed: boolean;
  };
  friday: {
    open: string | null;
    close: string | null;
    closed: boolean;
  };
  saturday: {
    open: string | null;
    close: string | null;
    closed: boolean;
  };
  sunday: {
    open: string | null;
    close: string | null;
    closed: boolean;
  };
  emergency_available?: boolean;
}
interface EditBusinessHoursModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentSchedule: Schedule;
  onUpdate: (schedule: Schedule) => Promise<void>;
}
const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;
const DAY_LABELS: Record<typeof DAYS[number], string> = {
  monday: 'Monday',
  tuesday: 'Tuesday',
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
  saturday: 'Saturday',
  sunday: 'Sunday'
};
export const EditBusinessHoursModal = ({
  open,
  onOpenChange,
  currentSchedule,
  onUpdate
}: EditBusinessHoursModalProps) => {
  const [schedule, setSchedule] = useState<Schedule>(currentSchedule);
  const [isLoading, setIsLoading] = useState(false);
  useEffect(() => {
    if (open) {
      setSchedule(currentSchedule);
    }
  }, [open, currentSchedule]);
  const handleDayToggle = (day: typeof DAYS[number]) => {
    setSchedule(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        closed: !prev[day].closed,
        open: prev[day].closed ? '09:00' : prev[day].open,
        close: prev[day].closed ? '17:00' : prev[day].close
      }
    }));
  };
  const handleTimeChange = (day: typeof DAYS[number], type: 'open' | 'close', value: string) => {
    setSchedule(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        [type]: value
      }
    }));
  };
  const handleApplyToWeekdays = () => {
    const mondaySchedule = schedule.monday;
    setSchedule(prev => ({
      ...prev,
      tuesday: {
        ...mondaySchedule
      },
      wednesday: {
        ...mondaySchedule
      },
      thursday: {
        ...mondaySchedule
      },
      friday: {
        ...mondaySchedule
      }
    }));
    toast.success('Applied Monday schedule to all weekdays');
  };
  const validateSchedule = (): boolean => {
    for (const day of DAYS) {
      const daySchedule = schedule[day];
      if (!daySchedule.closed) {
        if (!daySchedule.open || !daySchedule.close) {
          toast.error(`Please set both open and close times for ${DAY_LABELS[day]}`);
          return false;
        }
        if (daySchedule.open >= daySchedule.close) {
          toast.error(`Close time must be after open time for ${DAY_LABELS[day]}`);
          return false;
        }
      }
    }
    return true;
  };
  const handleSave = async () => {
    if (!validateSchedule()) {
      return;
    }
    setIsLoading(true);
    try {
      await onUpdate(schedule);
      toast.success('Business hours updated successfully');
      onOpenChange(false);
    } catch (error: any) {
      console.error('Update error:', error);
      toast.error(error.message || 'Failed to update business hours');
    } finally {
      setIsLoading(false);
    }
  };
  return <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-primary" />
            Edit Business Hours
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="flex justify-end">
            <Button type="button" variant="outline" size="sm" onClick={handleApplyToWeekdays} className="gap-2">
              <Copy className="w-4 h-4" />
              Apply Monday to Weekdays
            </Button>
          </div>

          {DAYS.map(day => <div key={day} className="space-y-2 p-4 border rounded-lg">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold">{DAY_LABELS[day]}</Label>
                <div className="flex items-center gap-2">
                  <Checkbox id={`${day}-closed`} checked={schedule[day].closed} onCheckedChange={() => handleDayToggle(day)} />
                  <label htmlFor={`${day}-closed`} className="text-sm font-medium cursor-pointer">
                    Closed
                  </label>
                </div>
              </div>

              {!schedule[day].closed && <div className="grid grid-cols-2 gap-4 mt-2">
                  <div className="space-y-1">
                    <Label htmlFor={`${day}-open`} className="text-sm">Open Time</Label>
                    <TimePicker value={schedule[day].open || '09:00'} onChange={value => handleTimeChange(day, 'open', value)} />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor={`${day}-close`} className="text-sm">Close Time</Label>
                    <TimePicker value={schedule[day].close || '17:00'} onChange={value => handleTimeChange(day, 'close', value)} />
                  </div>
                </div>}
            </div>)}

          <div className="flex items-center space-x-2 p-4 border rounded-lg bg-muted/20">
            <Checkbox id="emergency" checked={schedule.emergency_available || false} onCheckedChange={checked => setSchedule(prev => ({
            ...prev,
            emergency_available: checked as boolean
          }))} />
            <label htmlFor="emergency" className="text-sm font-medium cursor-pointer">Available for Emergency Services 24/7</label>
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isLoading}>
            {isLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Save Hours
          </Button>
        </div>
      </DialogContent>
    </Dialog>;
};